import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, TextField, Alert, IconButton, CircularProgress, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const MfaDialog = ({ open, onClose, onVerify, onResend, canResend = false, method = 'totp', message = '', isLoading = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const primaryColor = theme.palette.primary.main;
  const primaryLight = theme.palette.primary.light;
  const [digits, setDigits] = useState(new Array(6).fill(''));
  const inputsRef = useRef([]);

  useEffect(() => {
    if (open) {
      setDigits(new Array(6).fill(''));
      setTimeout(() => {
        const el = inputsRef.current[0];
        if (el) el.focus();
      }, 0);
    }
  }, [open]);

  const focusInput = (idx) => {
    const el = inputsRef.current[idx];
    if (el) el.focus();
    el.select && el.select();
  };

  const handleChange = (idx, val) => {
    const digit = (val || '').replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[idx] = digit || '';
    setDigits(next);
    if (digit && idx < 5) focusInput(idx + 1);
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      focusInput(idx - 1);
    }
    if (e.key === 'Enter') {
      attemptVerify();
    }
  };

  const handlePaste = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, 6);
    if (!paste) return;
    const next = new Array(6).fill('');
    paste.split('').forEach((d, i) => next[i] = d);
    setDigits(next);
    const nextIdx = Math.min(paste.length, 5);
    setTimeout(() => focusInput(nextIdx), 0);
    e.preventDefault();
  };

  const attemptVerify = () => {
    const code = digits.join('');
    if (!code || code.length < 6) return;
    if (onVerify) onVerify(code);
  };

  return (
    <Dialog
      open={open}
      onClose={() => { if (!isLoading) onClose(); }}
      aria-labelledby="mfa-dialog-title"
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 6, p: { xs: 2, sm: 3 }, overflow: 'hidden', boxShadow: `0px 20px 52px rgba(87, 87, 176, 0.12)` } }}
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle id="mfa-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'transparent', px: { xs: 1.5, sm: 2.5 }, py: { xs: 1, sm: 2 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: primaryColor }}>{t('login.enter_6_digit')}</Typography>
        <IconButton onClick={() => { if (!isLoading) onClose(); }} disabled={isLoading} aria-label={t('common.close') || 'Close'} sx={{ color: primaryColor }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers onPaste={handlePaste} sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 1.5 }, pb: { xs: 2, sm: 0 } }}>
        {message && <Alert severity="info" sx={{ mb: 2.25, borderRadius: 1, px: 1 }}>{message}</Alert>}

        <Typography variant="body2" sx={{ mb: 2.25, textAlign: 'center', color: '#666' }}>
          {method === 'totp' ? (t('login.open_authenticator') || t('login.enter_6_digit')) : (t('login.enter_6_digit'))}
        </Typography>

        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, justifyContent: 'center', mb: { xs: 1.75, sm: 2.25 } }}>
          {digits.map((d, i) => (
            <TextField
              key={i}
              inputRef={(el) => (inputsRef.current[i] = el)}
              value={d}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 1,
                style: { textAlign: 'center', fontSize: isSmall ? '1.05rem' : '1.25rem', padding: isSmall ? '10px' : '14px' },
                onPaste: handlePaste,
                onFocus: (e) => e.target.select && e.target.select()
              }}
              disabled={isLoading}
              variant="outlined"
              sx={{ width: { xs: 44, sm: 56 }, '& .MuiOutlinedInput-root': { borderRadius: 2, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor } } }}
            />
          ))}
        </Box>

        {canResend && (
          <Box sx={{ textAlign: 'center', mb: { xs: 1.25, sm: 1.5 } }}>
            <Button onClick={onResend} size="small" variant="text" sx={{ color: primaryColor, fontWeight: 700 }}>{t('login.resend_code')}</Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1.75, sm: 2.5 }, display: 'flex', gap: { xs: 1, sm: 1.5 } }}>
        <Button onClick={() => { if (!isLoading) onClose(); }} disabled={isLoading} sx={{ color: '#666', minWidth: 84 }}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={attemptVerify}
          disabled={isLoading || digits.join('').length < 6}
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{
            backgroundColor: primaryColor,
            borderRadius: 2,
            py: isSmall ? 1 : 1.25,
            fontSize: isSmall ? '0.95rem' : '1rem',
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': { backgroundColor: '#4a4a9d' },
            minWidth: 110
          }}
        >
          {isLoading ? (t('login.verifying') || 'Verifying') : (t('login.verify') || 'Verify')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MfaDialog;
